/**
 * Card de indicador do dashboard (Task 10 · redesign por perfil, Fase 0).
 *
 * Regra do brief: **todo indicador navega até os registros que o compõem**.
 * Por isso o card SEMPRE renderiza um link de drill-down, com a quantidade de
 * registros-fonte visível — o número do card e o tamanho da lista destino vêm
 * do mesmo seletor (`selectKpiRecords`), nunca de contas paralelas.
 *
 * Primeiro componente `.tsx` (JSX real) desta área — ver
 * `docs/architecture/ADR-0011-*.md`. Mesmo visual de antes (ícone em caixa
 * colorida, valor em destaque): NÃO existe badge de variação percentual — o
 * modelo `Kpi` (`dashboard/kpis.ts`) não carrega histórico/tendência, e
 * inventar um "+12%" aqui seria o mesmo erro que a régua do projeto proíbe
 * para o valor principal (nunca mostrar número que não vem de registro real).
 * Se um dia o backend publicar variação período-a-período, ela entra aqui —
 * não antes.
 *
 * Invariantes PRESERVADOS (contrato do dashboard — não mudam de comportamento):
 *  - `data-kpi`, `data-drilldown-route`, `data-drilldown-records`;
 *  - `aria-labelledby` + `id="kpi-<id>-label"`;
 *  - `data-kpi-value`, `data-kpi-pending`;
 *  - o link com `aria-label` descritivo e o texto `"<label> (<n>)"`;
 *  - estado `awaiting_source` com o texto "Fonte de dados ainda não publicada"
 *    e valor "—" (nunca 0).
 *
 * Nota de import: `React` explícito mesmo com runtime automático — ver o
 * comentário equivalente em `layout/AppShell.tsx` (necessário para
 * `tsx --test`, o motor de `npm test`).
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Clock3,
  DollarSign,
  MessageCircleWarning,
  Percent,
  RefreshCw,
  ShieldAlert,
  ShoppingCart,
  Timer,
  type LucideIcon
} from "lucide-react";
import { formatKpiValue } from "../format.js";
import { useCountUp } from "./motion.js";
import type { Kpi, KpiId } from "../dashboard/kpis.js";

/**
 * Ícone por indicador (puramente decorativo/ilustrativo — o rótulo textual
 * segue sendo o nome acessível do card). Mapa fechado: um `KpiId` novo cai no
 * fallback `DollarSign` em vez de quebrar o build.
 */
const ICON_BY_KPI: Record<KpiId, LucideIcon> = {
  faturamento: DollarSign,
  pedidos: ShoppingCart,
  margem_estimada: Percent,
  pedidos_pendentes: Clock3,
  falhas_sincronizacao: RefreshCw,
  estoque_critico: AlertTriangle,
  conversas_aguardando: MessageCircleWarning,
  garantias_abertas: ShieldAlert,
  sla_atendimento: Timer
};

export interface KpiCardProps {
  kpi: Kpi;
}

export function KpiCard({ kpi }: KpiCardProps): React.ReactElement {
  const recordCount = kpi.drillDown.recordIds.length;
  const awaiting = kpi.state === "awaiting_source";
  const Icon = ICON_BY_KPI[kpi.id] ?? DollarSign;
  // Contagem animada: anima a apresentação do MESMO valor que já veio do
  // seletor — nunca recalcula nem inventa número. `null` (fonte ausente)
  // passa direto pelo hook, então o card continua mostrando "—".
  const animatedValue = useCountUp(kpi.value);
  const displayValue =
    animatedValue === null || animatedValue === kpi.value
      ? formatKpiValue(kpi)
      : formatKpiValue({ ...kpi, value: Math.round(animatedValue) });
  // Estado "aguardando fonte": accent de borda à esquerda + selo textual.
  // Nunca cor sozinha. Elevação no hover marca "isto é clicável", nunca
  // decoração permanente.
  const cardClass = [
    "kpi-card group relative flex min-w-0 flex-col gap-3 rounded-panel border bg-surface p-5 shadow-s1",
    "transition-[color,background-color,border-color,box-shadow,transform] duration-base ease-s2",
    "hover:border-brand hover:shadow-s2 hover:-translate-y-0.5",
    awaiting ? "kpi-card--awaiting_source border-l-4 border-l-brand border-border" : "border-border"
  ].join(" ");

  return (
    <article
      className={cardClass}
      data-kpi={kpi.id}
      data-drilldown-route={kpi.drillDown.route}
      data-drilldown-records={String(recordCount)}
      aria-labelledby={`kpi-${kpi.id}-label`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="kpi-card__label text-sm font-extrabold text-muted" id={`kpi-${kpi.id}-label`}>
          {kpi.label}
        </h3>
        <span
          className={
            "kpi-card__icon grid h-[32px] w-[32px] shrink-0 place-items-center rounded-control " +
            (awaiting ? "bg-warning-soft text-warning" : "bg-surface-soft text-ink")
          }
        >
          <Icon className="h-4 w-4" aria-hidden focusable={false} />
        </span>
      </div>
      <p className="kpi-card__value text-[2.15rem] font-black leading-none tracking-tight text-ink tabular-nums" data-kpi-value={kpi.id}>
        {displayValue}
      </p>
      {awaiting ? (
        <p className="kpi-card__badge inline-flex w-fit items-center gap-1 rounded-pill bg-warning-soft px-2.5 py-0.5 text-xs font-bold text-warning" data-kpi-pending={kpi.id}>
          Fonte de dados ainda não publicada
        </p>
      ) : null}
      <p className="kpi-card__note text-sm leading-snug text-muted">{kpi.note}</p>
      <Link
        className="kpi-card__drilldown mt-auto inline-flex min-h-[40px] w-fit items-center gap-1 text-sm font-extrabold text-ink underline decoration-brand decoration-2 underline-offset-4 focus-visible:outline-none"
        to={kpi.drillDown.route}
        aria-label={`${kpi.drillDown.label}: ${recordCount} registro(s) que compõem ${kpi.label}`}
      >
        <span>{`${kpi.drillDown.label} (${recordCount})`}</span>
        <ArrowRight className="h-4 w-4" aria-hidden focusable={false} />
      </Link>
    </article>
  );
}

export interface KpiGridProps {
  kpis: Kpi[];
  title: string;
  description: string;
}

export function KpiGrid({ kpis, title, description }: KpiGridProps): React.ReactElement {
  return (
    <section className="kpi-grid flex flex-col gap-4" aria-label={title}>
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-extrabold text-ink">{title}</h2>
        <p className="kpi-grid__description max-w-prose text-sm text-muted">{description}</p>
      </div>
      <div className="kpi-grid__items grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((kpi) => (
          <KpiCard key={kpi.id} kpi={kpi} />
        ))}
      </div>
    </section>
  );
}
